/**
 * Very small Markdown subset for activity notes, agendas, outcomes and team
 * updates: headings, bold/italic, inline code, links, bullet and numbered
 * lists, fenced code, pipe tables, and paragraphs.
 *
 * Hand-rolled rather than pulling in a Markdown library because the input is
 * written by team members inside the portal and rendered right back to them —
 * a full parser plus a sanitizer is a lot of surface area for a handful of
 * formatting features. Everything is escaped first, so no raw HTML from the DB
 * ever reaches the DOM.
 *
 * Pure (no React) so `components/page.tsx` renders it on screen,
 * `lib/updates.ts` turns the same output into the HTML an update is emailed
 * as, and `scripts/updates-check.mjs` can assert both without a browser.
 */

export function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function inline(s: string): string {
  return (
    escapeHtml(s)
      // Links: only http(s) and mailto survive, so an escaped `javascript:`
      // payload can't become an href.
      .replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+|mailto:[^\s)]+)\)/g, (_m, text, href) => {
        return `<a href="${href}" target="_blank" rel="noopener noreferrer">${text}</a>`
      })
      .replace(/`([^`]+)`/g, '<code>$1</code>')
      .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
      .replace(/(^|[\s(])\*([^*\n]+)\*/g, '$1<em>$2</em>')
  )
}

/** `| a | b |` → ['a', 'b']. Outer pipes are optional, as in GFM. */
function tableCells(line: string): string[] {
  let s = line.trim()
  if (s.startsWith('|')) s = s.slice(1)
  if (s.endsWith('|')) s = s.slice(0, -1)
  return s.split('|').map((c) => c.trim())
}

/** The `|---|:---:|` row that turns the line above it into a table header. */
function isTableSeparator(line: string | undefined): boolean {
  if (line === undefined || !line.includes('-')) return false
  const cells = tableCells(line)
  return cells.length > 0 && cells.every((c) => /^:?-{1,}:?$/.test(c))
}

/**
 * A table starts at a line with a pipe in it whose NEXT line is a separator.
 * Requiring the separator is what keeps an ordinary sentence with a `|` in it
 * ("A | B testing") from turning into a one-cell table.
 */
function isTableStart(lines: string[], i: number): boolean {
  return lines[i].includes('|') && isTableSeparator(lines[i + 1])
}

export function renderMarkdown(src: string): string {
  const lines = (src ?? '').replace(/\r\n/g, '\n').split('\n')
  const out: string[] = []
  let i = 0

  while (i < lines.length) {
    const line = lines[i]

    if (line.trim().startsWith('```')) {
      const body: string[] = []
      i++
      while (i < lines.length && !lines[i].trim().startsWith('```')) body.push(lines[i++])
      i++ // closing fence
      out.push(`<pre><code>${escapeHtml(body.join('\n'))}</code></pre>`)
      continue
    }

    const heading = line.match(/^(#{1,6})\s+(.*)$/)
    if (heading) {
      const level = Math.min(heading[1].length + 1, 4) // never emit an h1
      out.push(`<h${level}>${inline(heading[2])}</h${level}>`)
      i++
      continue
    }

    if (isTableStart(lines, i)) {
      const head = tableCells(line)
      i += 2 // header + separator
      const rows: string[][] = []
      while (i < lines.length && lines[i].trim() !== '' && lines[i].includes('|')) {
        rows.push(tableCells(lines[i++]))
      }
      // Every row is padded or cut to the header's width: a ragged table is the
      // usual typing slip, and a cell that falls off the end renders nowhere.
      const width = head.length
      const fit = (cells: string[]) =>
        Array.from({ length: width }, (_, k) => inline(cells[k] ?? ''))
      out.push(
        `<table><thead><tr>${fit(head)
          .map((c) => `<th>${c}</th>`)
          .join('')}</tr></thead><tbody>${rows
          .map((r) => `<tr>${fit(r).map((c) => `<td>${c}</td>`).join('')}</tr>`)
          .join('')}</tbody></table>`,
      )
      continue
    }

    if (/^\s*[-*+]\s+/.test(line)) {
      const items: string[] = []
      while (i < lines.length && /^\s*[-*+]\s+/.test(lines[i])) {
        items.push(`<li>${inline(lines[i].replace(/^\s*[-*+]\s+/, ''))}</li>`)
        i++
      }
      out.push(`<ul>${items.join('')}</ul>`)
      continue
    }

    if (/^\s*\d+[.)]\s+/.test(line)) {
      const items: string[] = []
      while (i < lines.length && /^\s*\d+[.)]\s+/.test(lines[i])) {
        items.push(`<li>${inline(lines[i].replace(/^\s*\d+[.)]\s+/, ''))}</li>`)
        i++
      }
      out.push(`<ol>${items.join('')}</ol>`)
      continue
    }

    if (line.trim() === '') {
      i++
      continue
    }

    // Paragraph: gather until a blank line or the start of another block.
    const para: string[] = []
    while (
      i < lines.length &&
      lines[i].trim() !== '' &&
      !/^\s*([-*+]|\d+[.)])\s+/.test(lines[i]) &&
      !/^#{1,6}\s/.test(lines[i]) &&
      !lines[i].trim().startsWith('```') &&
      !isTableStart(lines, i)
    ) {
      para.push(lines[i++])
    }
    out.push(`<p>${inline(para.join(' '))}</p>`)
  }

  return out.join('')
}
